import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as IconFullLogo } from "../../../assets/svg/linkedscope-logo.svg";
import { ReactComponent as IconAlert } from "../../../assets/svg/icon-alert.svg";
import {
  login,
  completeGoogleLogin,
  resendEmailLoginCode,
  startGoogleLogin,
  verifyEmailLoginCode,
} from "../../../useCase/authUseCase";
import GoogleSignInButton from "../googleSignInButton";
import { signup as requestSignupEmail } from "../../../useCase/signupUsecase";
import { Password } from "primereact/password";
import { Checkbox } from "primereact/checkbox";
import { useAuthLanguage } from "hooks/useAuthLanguage";
import { buildAuthPageRoute } from "../../../utils/authLanguage";
import { useTranslation } from 'react-i18next';
import { clearAccessToken, decodeAccessToken, isAccessTokenValid, setAccessToken } from "utils/authToken";
import { setDefaultLast30Days } from "utils/dateDefaults";
import { setForceUpgradeUi } from "../../../utils/billingUiGate";
import ResetPasswordModal from "../resetPasswordModal";
import AuthSplitLayout from "../authSplitLayout";
import usePageTitle from "../../../utils/pageTitle";
import useAuthPageMetadata from "../../../hooks/useAuthPageMetadata";
import { normalizeLanguage } from "utils/locale";
import { loadBillingSummary } from "../../../useCase/billingUseCase";
import { warmCompanyLogoCacheForSession } from "../../../utils/companyLogoWarmup";
import { createAuthPerformanceTrace } from "../../../utils/authPerformance";
import {
  canAccessUpgradeRoute,
  getCurrentUserRole,
} from "../../../utils/userRole";
import { resolveBillingBlockedRoute } from "../../../utils/billingNavigation";
import i18n from "../../../i18n";
import { buildContactPageUrl, buildPrivacyPolicyUrl, buildTermsOfUseUrl } from "../../../utils/contactUrl";
import {
  buildNewAccountRoute,
  evaluateTokenBillingGate,
  reconcileShouldUpgradeWithSummary,
  resolveAccountLandingRoute,
  resolveTokenSessionRoute,
} from "../../../utils/loginRouting";
import {
  GOOGLE_AUTH_RETURN_STATUS,
  readGoogleAuthReturn,
  takeHandoffVerifier,
} from "../../../utils/googleLoginHandoff";
import { takePendingConsentReturn } from "../../../utils/oauthConsentReturn";

const unwrapPayload = (payload) => {
  let normalized = payload;
  if (normalized && typeof normalized === "object" && "data" in normalized) {
    normalized = normalized.data;
  }
  if (normalized && typeof normalized === "object" && "data" in normalized) {
    normalized = normalized.data;
  }
  return normalized && typeof normalized === "object" ? normalized : {};
};

const isEmailVerificationRateLimitError = error =>
  ["EMAIL_LOGIN_RATE_LIMITED", "EMAIL_LOGIN_RESEND_TOO_SOON"].includes(String(error?.code || ""));

function detectTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && String(tz).trim() ? String(tz).trim() : null;
  } catch (err) {
    return null;
  }
}

function Login() {
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState("");
  const [userPassword, setUserPassword] = useState();
  const [signinClick, setSigninClick] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackType, setFeedbackType] = useState("error");
  const [isResendingConfirmation, setIsResendingConfirmation] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [isGoogleAuthBusy, setIsGoogleAuthBusy] = useState(false);
  const [twoFactorChallenge, setTwoFactorChallenge] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [twoFactorMaskedEmail, setTwoFactorMaskedEmail] = useState("");
  const [twoFactorResendCooldown, setTwoFactorResendCooldown] = useState(0);
  const [rememberBrowser, setRememberBrowser] = useState(false);
  // Read once: the effect below removes the Google parameters from the URL.
  const googleAuthReturnRef = useRef(null);
  if (googleAuthReturnRef.current === null) {
    googleAuthReturnRef.current = readGoogleAuthReturn(window.location);
  }
  const [isCompletingGoogleLogin, setIsCompletingGoogleLogin] = useState(
    googleAuthReturnRef.current.status === GOOGLE_AUTH_RETURN_STATUS.CODE
  );

  const { currentLanguage, changeLanguage, languageOptions } = useAuthLanguage();

  const { t } = useTranslation("commonTranslation");
  const { t: routes } = useTranslation("routes");
  usePageTitle(t("sections.login"));
  const authT = i18n.getFixedT(currentLanguage, "commonTranslation");
  useAuthPageMetadata("/login", currentLanguage, authT("auth.login.info"));
  const authErrorsT = i18n.getFixedT(currentLanguage, "errors");

  const supportContactLink = buildContactPageUrl(currentLanguage);
  const signupRoute = buildAuthPageRoute('/signup', currentLanguage, window.location.search);
  const supportLinkText = authT("auth.supportLink");
  const connectedSupportLinkText = supportLinkText
    ? `${supportLinkText.charAt(0).toLowerCase()}${supportLinkText.slice(1)}`
    : supportLinkText;

  const setErrorFeedback = (message) => {
    setFeedbackType("error");
    setFeedbackMessage(message);
  };

  const setInfoFeedback = (message) => {
    setFeedbackType("info");
    setFeedbackMessage(message);
  };

  useEffect(() => {
    if (twoFactorResendCooldown <= 0) return;
    const timerId = setTimeout(() => {
      setTwoFactorResendCooldown((current) => Math.max(0, current - 1));
    }, 1000);
    return () => clearTimeout(timerId);
  }, [twoFactorResendCooldown]);

  const getGenericFallbackMessage = () => (
    <span>
      {authT("auth.genericError")}{" "}
      <a href={supportContactLink} target="_blank" rel="noopener noreferrer">
        {connectedSupportLinkText}
      </a>
    </span>
  );

  // Completes the return from Google: the one-time code in the URL fragment is
  // exchanged for the JWT, then the app opens on the route from its claims.
  useEffect(() => {
    const googleReturn = googleAuthReturnRef.current;
    if (googleReturn.status === GOOGLE_AUTH_RETURN_STATUS.NONE) return;
    window.history.replaceState({}, "", googleReturn.cleanedUrl);

    const failGoogleLogin = () => {
      // A failed Google attempt must not fall back into a previous session.
      clearAccessToken();
      setIsCompletingGoogleLogin(false);
      setErrorFeedback(getGenericFallbackMessage());
    };

    if (googleReturn.status === GOOGLE_AUTH_RETURN_STATUS.ERROR) {
      takeHandoffVerifier();
      failGoogleLogin();
      return;
    }

    completeGoogleLogin(googleReturn.code)
      .then(enterGoogleSession)
      .catch(failGoogleLogin);
  }, []);

  // Clears the Google spinner when the browser restores this page from its
  // back-forward cache after the user left for Google.
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) setIsGoogleAuthBusy(false);
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const resolveInitialRoute = async () => {
      // The Google return effect decides the route once the code is exchanged.
      if (googleAuthReturnRef.current.status === GOOGLE_AUTH_RETURN_STATUS.CODE) return;
      if (!isAccessTokenValid()) {
        clearAccessToken();
        return;
      }

      const tokenData = decodeAccessToken() || {};
      const tokenLanguage = normalizeLanguage(
        tokenData?.language || localStorage.getItem("i18nextLng")
      );
      localStorage.setItem("i18nextLng", tokenLanguage);
      if (tokenData?.requiresNewAccount === true) {
        setForceUpgradeUi(tokenData?.accountId, false);
        if (i18n.resolvedLanguage !== tokenLanguage) {
          i18n.changeLanguage(tokenLanguage);
        }
        navigate(buildNewAccountRoute(tokenLanguage), { replace: true });
        return;
      }
      // An AI app connection (MCP OAuth) waiting for consent comes before the app.
      const consentRoute = takePendingConsentReturn();
      if (consentRoute) {
        navigate(consentRoute, { replace: true });
        return;
      }
      const tokenBillingGate = evaluateTokenBillingGate(tokenData);
      const { tokenPlanId, endTrialDate, isTrialInFuture } = tokenBillingGate;
      let { shouldUpgrade } = tokenBillingGate;

      const accountId = tokenData.accountId ?? null;
      const primeBillingCache = async () => {
        if (!accountId) return null;
        try {
          const summary = unwrapPayload(await loadBillingSummary());
          if (!summary || typeof summary !== "object") return null;
          sessionStorage.setItem(
            `billing_summary_cache_${accountId}`,
            JSON.stringify(summary)
          );
          return summary;
        } catch {
          return null;
        }
      };

      const logoPrefetchPromise = warmCompanyLogoCacheForSession({
        accountId,
        accountName: tokenData?.accountName ?? null,
        linkedinCompanyId: tokenData?.linkedinCompanyId ?? null,
        prefetchedLogoUrl: tokenData?.companyLogoUrl ?? null,
        prefetchedLogoUrlExpiresAt: tokenData?.companyLogoUrlExpiresAt ?? null,
        requireLogo: true,
        throwOnFailure: false,
        logoFetchAttempts: 2,
        logoFetchRetryDelayMs: 180,
        allowRemoteCompanyIdLookup: false,
      });

      if (!shouldUpgrade) {
        const [, summary] = await Promise.all([
          logoPrefetchPromise,
          primeBillingCache(),
        ]);
        shouldUpgrade = reconcileShouldUpgradeWithSummary({
          currentShouldUpgrade: shouldUpgrade,
          summary,
          tokenPlanId,
          endTrialDate,
          isTrialInFuture,
        });
      } else {
        await logoPrefetchPromise;
      }

      if (cancelled) return;
      setForceUpgradeUi(tokenData?.accountId, shouldUpgrade);
      if (i18n.resolvedLanguage !== tokenLanguage) {
        i18n.changeLanguage(tokenLanguage);
      }
      const canAccessUpgrade = canAccessUpgradeRoute(
        getCurrentUserRole(tokenData || {})
      );
      const billingBlockedRoute = resolveBillingBlockedRoute({
        canAccessUpgrade,
        settingsRoute: routes("settings"),
        upgradeRoute: "/upgrade",
        tokenData,
      });
      navigate(
        shouldUpgrade
          ? billingBlockedRoute
          : resolveAccountLandingRoute({ tokenData, settingsRoute: routes("settings") }),
        { replace: true }
      );
    };

    resolveInitialRoute();
    return () => {
      cancelled = true;
    };
  }, [navigate, routes]);

  const handleResendConfirmation = async () => {
    if (!userEmail) {
      setErrorFeedback(authT("toastMessages.validation.requiredEmail"));
      return;
    }

    if (!userEmail.includes("@")) {
      setErrorFeedback(authT("toastMessages.validation.invalidEmail"));
      return;
    }

    setIsResendingConfirmation(true);
    try {
      const detectedTimezone = detectTimezone();
      await requestSignupEmail({
        email: userEmail,
        language: currentLanguage,
        timezone: detectedTimezone || undefined,
      });
      setInfoFeedback(authT("auth.signup.emailSentNewUser"));
    } catch (error) {
      setErrorFeedback(getGenericFallbackMessage());
    } finally {
      setIsResendingConfirmation(false);
    }
  };

  const getLoginErrorMessage = (error) => {
    const code = String(error?.code || "");
    const rawMessage = String(error?.message || error?.error || "").toLowerCase();

    if (isEmailVerificationRateLimitError(error)) {
      return getEmailVerificationErrorMessage(error);
    }

    if (code === "error.400.invalid_password" || /invalid password|senha incorreta/.test(rawMessage)) {
      return authErrorsT("error.400.invalid_password");
    }

    if (code === "error.400.user_not_found" || /invalid user|user not found|usuario nao encontrado/.test(rawMessage)) {
      return (
        <span>
          {authT("auth.login.userNotFound")}{" "}
          <a href={signupRoute}>{authT("buttons.createNewAccount")}</a>
        </span>
      );
    }

    if (code === "error.400.user_blocked" || /user blocked|usuario bloqueado/.test(rawMessage)) {
      return (
        <span>
          {authT("auth.login.userBlocked")}{" "}
          <a href={supportContactLink} target="_blank" rel="noopener noreferrer">
            {authT("auth.supportLink")}
          </a>
        </span>
      );
    }

    if (code === "error.400.account_blocked" || /conta bloqueada|conta inativa|account blocked|account inactive/.test(rawMessage)) {
      return (
        <span>
          {authT("auth.login.accountBlocked")}{" "}
          <a href={supportContactLink} target="_blank" rel="noopener noreferrer">
            {authT("auth.supportLink")}
          </a>
        </span>
      );
    }

    if (code === "error.400.user_not_confirmed" || /not confirmed|nao confirmado/.test(rawMessage)) {
      return (
        <span>
          {authT("auth.login.userNotConfirmed")}{" "}
          <a
            href="#resend-confirmation"
            onClick={(event) => {
              event.preventDefault();
              handleResendConfirmation();
            }}
          >
            {authT("auth.resendConfirmationLink")}
          </a>
        </span>
      );
    }

    return getGenericFallbackMessage();
  };

  const onSigninClick = async () => {
    if (!userEmail) {
      setErrorFeedback(authT("toastMessages.validation.requiredEmail"));
      return;
    }

    if (!userEmail.includes("@")) {
      setErrorFeedback(authT("toastMessages.validation.invalidEmail"));
      return;
    }

    if (!userPassword) {
      setErrorFeedback(authT("toastMessages.validation.requiredPassword"));
      return;
    }

    const perf = createAuthPerformanceTrace("login.submit");
    setSigninClick(true);
    setFeedbackMessage("");
    perf.mark("start");
    const detectedTimezone = detectTimezone();
    try {
      const response = await login({
        email: userEmail,
        password: userPassword,
        timezone: detectedTimezone || undefined,
      });
      perf.mark("authenticate_ok");

      if (response?.requiresTwoFactor === true && response?.challengeToken) {
        setTwoFactorChallenge(response.challengeToken);
        setTwoFactorMaskedEmail(response.maskedEmail || "");
        setTwoFactorResendCooldown(Number(response.resendAfter || 60));
        setTwoFactorCode("");
        setUserPassword("");
        perf.flush({ success: true, requiresTwoFactor: true });
        return;
      }

      const tokenData = decodeAccessToken() || {};
      const nextLanguage = normalizeLanguage(
        tokenData?.language || response?.language || localStorage.getItem("i18nextLng")
      );
      localStorage.setItem("i18nextLng", nextLanguage);
      setDefaultLast30Days();
      if (tokenData?.requiresNewAccount === true) {
        setForceUpgradeUi(tokenData?.accountId, false);
        if (i18n.resolvedLanguage !== nextLanguage) {
          i18n.changeLanguage(nextLanguage);
        }
        const targetRoute = buildNewAccountRoute(nextLanguage);
        perf.mark("route_resolved", { targetRoute, requiresNewAccount: true });
        navigate(targetRoute, { replace: true });
        perf.flush({ success: true });
        return;
      }

      const consentRoute = takePendingConsentReturn();
      if (consentRoute) {
        if (i18n.resolvedLanguage !== nextLanguage) {
          i18n.changeLanguage(nextLanguage);
        }
        navigate(consentRoute, { replace: true });
        perf.flush({ success: true });
        return;
      }

      const tokenBillingGate = evaluateTokenBillingGate(tokenData);
      const { tokenPlanId, endTrialDate, isTrialInFuture } = tokenBillingGate;
      let { shouldUpgrade } = tokenBillingGate;

      const primeBillingCache = async () => {
        if (!tokenData?.accountId) return null;
        try {
          const summary = unwrapPayload(await loadBillingSummary());
          if (!summary || typeof summary !== "object") return null;
          sessionStorage.setItem(
            `billing_summary_cache_${tokenData.accountId}`,
            JSON.stringify(summary)
          );
          return summary;
        } catch (error) {
          return null;
        }
      };

      const accountId = tokenData.accountId ?? null;
      const logoPrefetchPromise = warmCompanyLogoCacheForSession({
        accountId,
        accountName: tokenData?.accountName ?? null,
        linkedinCompanyId: tokenData?.linkedinCompanyId ?? null,
        prefetchedLogoUrl: tokenData?.companyLogoUrl ?? null,
        prefetchedLogoUrlExpiresAt: tokenData?.companyLogoUrlExpiresAt ?? null,
        requireLogo: true,
        throwOnFailure: false,
        logoFetchAttempts: 2,
        logoFetchRetryDelayMs: 180,
        allowRemoteCompanyIdLookup: false,
      });

      const [_, summary] = await Promise.all([
        logoPrefetchPromise,
        primeBillingCache(),
      ]);
      perf.mark("warmups_done");

      shouldUpgrade = reconcileShouldUpgradeWithSummary({
        currentShouldUpgrade: shouldUpgrade,
        summary,
        tokenPlanId,
        endTrialDate,
        isTrialInFuture,
      });

      setForceUpgradeUi(tokenData?.accountId, shouldUpgrade);
      const canAccessUpgrade = canAccessUpgradeRoute(
        getCurrentUserRole(tokenData || {})
      );

      let targetRoute = resolveBillingBlockedRoute({
        canAccessUpgrade,
        settingsRoute: routes("settings"),
        upgradeRoute: "/upgrade",
        tokenData,
        summary,
      });
      if (!shouldUpgrade) {
        targetRoute = resolveAccountLandingRoute({
          tokenData,
          settingsRoute: routes("settings"),
        });
      }
      perf.mark("route_resolved", { targetRoute, shouldUpgrade });

      if (i18n.resolvedLanguage !== nextLanguage) {
        i18n.changeLanguage(nextLanguage);
      }
      navigate(targetRoute, { replace: true });
      perf.flush({ success: true });
    } catch (error) {
      setErrorFeedback(getLoginErrorMessage(error));
      perf.flush({ success: false, reason: "authenticate_failed" });
    } finally {
      setSigninClick(false);
    }
  };

  // Enters the app straight from the token claims. The app shell loads the
  // billing summary and re-applies billing gating, so nothing is awaited here.
  const enterGoogleSession = (token) => {
    setAccessToken(token);
    setDefaultLast30Days();
    const tokenData = decodeAccessToken(token) || {};
    const { route, language, shouldUpgrade } = resolveTokenSessionRoute({
      tokenData,
      settingsRoute: routes("settings"),
      fallbackLanguage: localStorage.getItem("i18nextLng"),
    });
    localStorage.setItem("i18nextLng", language);
    setForceUpgradeUi(tokenData?.accountId, shouldUpgrade);
    if (i18n.resolvedLanguage !== language) {
      i18n.changeLanguage(language);
    }
    // Only caches the logo carried by the token; the navbar fetches it otherwise,
    // so the login does not race it with a second request.
    warmCompanyLogoCacheForSession({
      accountId: tokenData?.accountId ?? null,
      accountName: tokenData?.accountName ?? null,
      linkedinCompanyId: tokenData?.linkedinCompanyId ?? null,
      prefetchedLogoUrl: tokenData?.companyLogoUrl ?? null,
      prefetchedLogoUrlExpiresAt: tokenData?.companyLogoUrlExpiresAt ?? null,
      allowRemoteCompanyIdLookup: false,
      allowRemoteLogoFetch: false,
    }).catch(() => {});
    const consentRoute = tokenData?.requiresNewAccount === true ? null : takePendingConsentReturn();
    navigate(consentRoute || route, { replace: true });
  };

  const handleGoogleAuthClick = async () => {
    if (isGoogleAuthBusy) return;
    setIsGoogleAuthBusy(true);
    setFeedbackMessage("");
    try {
      await startGoogleLogin({
        mode: "login",
        language: currentLanguage,
        timezone: detectTimezone() || undefined,
      });
    } catch (error) {
      setErrorFeedback(getGenericFallbackMessage());
      setIsGoogleAuthBusy(false);
    }
  };

  const getEmailVerificationErrorMessage = (error) => {
    const code = String(error?.code || "");
    if (isEmailVerificationRateLimitError(error)) {
      return authT("auth.loginVerification.rateLimited");
    }
    if (code === "EMAIL_LOGIN_CODE_INVALID" || code === "EMAIL_LOGIN_CHALLENGE_INVALID") {
      return authT("auth.loginVerification.invalidCode");
    }
    return getGenericFallbackMessage();
  };

  const handleVerifyEmailCode = async () => {
    const normalizedCode = String(twoFactorCode || "").trim();
    if (!/^\d{6}$/.test(normalizedCode)) {
      setErrorFeedback(authT("auth.loginVerification.invalidCode"));
      return;
    }
    setSigninClick(true);
    setFeedbackMessage("");
    try {
      const response = await verifyEmailLoginCode({
        challengeToken: twoFactorChallenge,
        code: normalizedCode,
        rememberBrowser,
      });
      enterGoogleSession(response.token);
    } catch (error) {
      setErrorFeedback(getEmailVerificationErrorMessage(error));
    } finally {
      setSigninClick(false);
    }
  };

  const handleResendEmailCode = async () => {
    if (signinClick || twoFactorResendCooldown > 0) return;
    setSigninClick(true);
    setFeedbackMessage("");
    try {
      const response = await resendEmailLoginCode({ challengeToken: twoFactorChallenge });
      setTwoFactorChallenge(response.challengeToken);
      setTwoFactorMaskedEmail(response.maskedEmail || twoFactorMaskedEmail);
      setTwoFactorResendCooldown(Number(response.resendAfter || 60));
      setTwoFactorCode("");
    } catch (error) {
      setErrorFeedback(getEmailVerificationErrorMessage(error));
    } finally {
      setSigninClick(false);
    }
  };

  const handleBackFromEmailCode = () => {
    setRememberBrowser(false);
    setTwoFactorChallenge("");
    setTwoFactorCode("");
    setTwoFactorMaskedEmail("");
    setTwoFactorResendCooldown(0);
    setFeedbackMessage("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (twoFactorChallenge) {
      handleVerifyEmailCode();
      return;
    }
    onSigninClick();
  };
  const isPasswordAuthBusy = signinClick || isResendingConfirmation;
  const isBusy = isPasswordAuthBusy || isGoogleAuthBusy;

  if (isCompletingGoogleLogin) {
    return (
      <div className="login-component login-component-full" aria-busy="true">
        <div className="spinner spinner-dark"></div>
      </div>
    );
  }

  return (
    <>
      <AuthSplitLayout currentLanguage={currentLanguage} languageOptions={languageOptions} changeLanguage={changeLanguage}>
        <form className="login-card" onSubmit={handleSubmit} noValidate>
          <div className="login-logo">
            <IconFullLogo />
            <h1 className="login-heading">{t("auth.login.info")}</h1>
          </div>

          {feedbackMessage && (!twoFactorChallenge || feedbackType === "error") && (
            <div className={`message ${feedbackType === "info" ? "msg-info" : "msg-error"}`}>
              <IconAlert /> {feedbackMessage}
            </div>
          )}

          {twoFactorChallenge ? (<>
            <div className="message msg-info login-verification-info">
              <IconAlert />
              <div className="login-verification-info-content">
                <span>{t("auth.loginVerification.description", { email: twoFactorMaskedEmail })}</span>
                {twoFactorResendCooldown > 0 ? (
                  <strong>
                    {t("auth.loginVerification.resendIn", {
                      count: String(twoFactorResendCooldown).padStart(2, "0"),
                    })}
                  </strong>
                ) : (
                  <a href="#resend-login-code" className={isBusy ? "disabled-link" : ""} aria-disabled={isBusy}
                    onClick={event => {
                      event.preventDefault();
                      if (!isBusy) handleResendEmailCode();
                    }}>
                    {t("auth.loginVerification.resend")}
                  </a>
                )}
              </div>
            </div>

            <label>
              {t("auth.loginVerification.codeLabel")}
              <input
                type="text"
                className="ls-input"
                value={twoFactorCode}
                disabled={isBusy}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
                onChange={(event) => {
                  setTwoFactorCode(event.target.value.replace(/\D/g, "").slice(0, 6));
                  if (feedbackMessage) setFeedbackMessage("");
                }}
              />
            </label>

            <label htmlFor="login-trust-browser" className="login-trust-browser checkbox-option">
              <Checkbox inputId="login-trust-browser" checked={rememberBrowser} disabled={isBusy}
                onChange={event => setRememberBrowser(Boolean(event.checked))} />
              <span>{t("auth.loginVerification.rememberBrowser")}</span>
            </label>

            <button
              type="submit"
              className={`ls-btn ls-btn-big ls-btn-pink ${isPasswordAuthBusy ? "disabled" : ""}`}
              disabled={isBusy}
            >
              {isPasswordAuthBusy ? <div className="spinner"></div> : t("auth.loginVerification.verify")}
            </button>

            <div className="divider">
              <span>{t("tags.or")}</span>
            </div>

            <button type="button" className="ls-btn" onClick={handleBackFromEmailCode} disabled={isBusy}>
              {t("auth.loginVerification.useDifferentEmail")}
            </button>
          </>) : (<>
          <label>
            {t("fields.email")}
            <input
              type="email"
              className="ls-input"
              value={userEmail}
              disabled={isBusy}
              onChange={(e) => {
                setUserEmail(e.target.value);
                if (feedbackMessage) setFeedbackMessage("");
              }}
            />
          </label>

          <label>
            <span className="forgot-password">
              {t("fields.password")}
              <span onClick={() => setIsResetPasswordModalOpen(true)}>
                {t("auth.resetPassword.forgotPassword")}
              </span>
            </span>
            <Password
              disabled={isBusy}
              onChange={(e) => {
                setUserPassword(e.target.value);
                if (feedbackMessage) setFeedbackMessage("");
              }}
              feedback={false}
              toggleMask
            />
          </label>

          <button
            type="submit"
            className={`ls-btn ls-btn-big ls-btn-pink ${isPasswordAuthBusy ? "disabled" : ""}`}
            disabled={isBusy}
          >
            {isPasswordAuthBusy
              ? <div className="spinner"></div>
              : t("buttons.login")
            }
          </button>

          <GoogleSignInButton
            label={t("buttons.signInWithGoogle")}
            onClick={handleGoogleAuthClick}
            disabled={isBusy}
            loading={isGoogleAuthBusy}
          />

          <div className="divider">
            <span>{t("tags.or")}</span>
          </div>

          <button
            type="button"
            className="ls-btn"
            onClick={() => navigate(signupRoute)}
          >
            {t("buttons.signUpText")}
          </button>
          </>)}

          <p>
            {t("auth.legal.intro")}{" "}
            <strong>
              <a href={buildTermsOfUseUrl(currentLanguage)} target="_blank" rel="noopener noreferrer">
                {t("auth.legal.terms")}
              </a>
            </strong>{" "}
            {t("auth.legal.and")}{" "}
            <strong>
              <a href={buildPrivacyPolicyUrl(currentLanguage)} target="_blank" rel="noopener noreferrer">
                {t("auth.legal.privacy")}
              </a>
            </strong>
          </p>
        </form>
      </AuthSplitLayout>

      <ResetPasswordModal
        isModalOpen={isResetPasswordModalOpen}
        onClose={() => setIsResetPasswordModalOpen(false)}
        initialEmail={userEmail}
      />
    </>
  );
}

export default Login;
